import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useState, useEffect } from 'react';
import { NavLink as RouterLink, useLocation } from 'react-router-dom';
// @mui
import { AppBar, Box, Container, Link, Stack, Toolbar, Typography, Select, MenuItem } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
// hooks
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
// utils
import cssStyles from '../../../utils/cssStyles';
// config
import { HEADER, NAVBAR } from '../../../config';
// components
import Iconify from '../../../components/Iconify';
import Logo from '../../../components/Logo';
import { IconButtonAnimate } from '../../../components/animate';
//
import useAuth from '../../../hooks/useAuth';
import { openModal } from '../../../redux/slices/authModal';
import AccountPopover from './AccountPopover';
import navConfig from './MenuConfig';
import MenuDesktop from './MenuDesktop';
import MenuMobile from './MenuMobile';
import CreditsIndicator from './CreditsIndicator';

// ----------------------------------------------------------------------

const RootStyle = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'isCollapse' && prop !== 'isOffset' && prop !== 'verticalLayout',
})(({ isCollapse, isOffset, verticalLayout, theme }) => ({
  ...cssStyles(theme).bgBlur(),
  boxShadow: 'none',
  height: HEADER.MOBILE_HEIGHT,
  zIndex: theme.zIndex.appBar + 1,
  transition: theme.transitions.create(['width', 'height'], {
    duration: theme.transitions.duration.shorter,
  }),
  [theme.breakpoints.up('lg')]: {
    height: HEADER.DASHBOARD_DESKTOP_HEIGHT,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH + 1}px)`,
    ...(isCollapse && {
      width: `calc(100% - ${NAVBAR.DASHBOARD_COLLAPSE_WIDTH}px)`,
    }),
    ...(isOffset && {
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
    }),
    ...(verticalLayout && {
      width: '100%',
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
      backgroundColor: theme.palette.background.default,
    }),
  },
}));


const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
  height: HEADER.MOBILE_HEIGHT,
  transition: theme.transitions.create(['height', 'background-color'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
  [theme.breakpoints.up('md')]: {
    height: HEADER.MAIN_DESKTOP_HEIGHT,
  },
}));

const ToolbarShadowStyle = styled('div')(({ theme }) => ({
  left: 0,
  right: 0,
  bottom: 0,
  height: 24,
  zIndex: -1,
  margin: 'auto',
  borderRadius: '50%',
  position: 'absolute',
  width: `calc(100% - 48px)`,
  boxShadow: theme.customShadows.z8,
}));

// ----------------------------------------------------------------------

DashboardHeader.propTypes = {
  onOpenSidebar: PropTypes.func,
  isCollapse: PropTypes.bool,
  verticalLayout: PropTypes.bool,
};

const SpaceSelector = styled(Select)(({ theme }) => ({
  marginRight: theme.spacing(2),
  minWidth: 120,
}));

export default function DashboardHeader({ onOpenSidebar, isCollapse = false, verticalLayout = false }) {
  const { pathname } = useLocation();

  const dispatch = useDispatch();

  const isOffset = useOffSetTop(HEADER.DASHBOARD_DESKTOP_HEIGHT) && !verticalLayout;

  const isDesktop = useResponsive('up', 'lg');

  const theme = useTheme();

  const { user } = useAuth();
  
  const isHome = false;

  const finalConfig = navConfig;

  const [open, setOpen] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedSpace, setSelectedSpace] = useState(() => {
    const savedSpace = localStorage.getItem('selectedSpace');
    return savedSpace ? JSON.parse(savedSpace) : null;
  });
  const [spaces, setSpaces] = useState([{'id': 1, 'name': 'Space 1'}, {'id': 2, 'name': 'Space 2'}]);
  const [isSpacesLoaded, setIsSpacesLoaded] = useState(false);

  useEffect(() => {
    if (drawerOpen) {
      handleDrawerClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleOpen = () => {
    setOpen(!open);
  };

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleSpaceChange = (event) => {
    const newSelectedSpace = event.target.value;
    setSelectedSpace(newSelectedSpace);
    localStorage.setItem('selectedSpace', JSON.stringify(newSelectedSpace));
  };

  const loadSpaces = async () => {
    if (!isSpacesLoaded) {
      try {
        // Replace this with your actual API call
        const response = await fetch('/api/spaces');
        const data = await response.json();
        setSpaces(data);
        setIsSpacesLoaded(true);

        if (!selectedSpace && data.length > 0) {
          setSelectedSpace(data[0]);
          localStorage.setItem('selectedSpace', JSON.stringify(data[0]));
        }
      } catch (error) {
        console.error('Error loading spaces:', error);
      }
    }
  };

  return (
    <RootStyle isCollapse={isCollapse} isOffset={isOffset} verticalLayout={verticalLayout}>
      <Toolbar
        sx={{
          minHeight: '100% !important',
          px: { lg: 5 },
        }}
      >
        <Container
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >

          {/* {isDesktop && <Logo sx={{ mr: 2.5 }} />} */}

        {!isDesktop && (
          <IconButtonAnimate onClick={onOpenSidebar} sx={{ mr: 1, color: 'text.primary' }}>
            <Iconify icon="eva:menu-2-fill" />
          </IconButtonAnimate>
        )}




          <Box sx={{ flexGrow: 1 }} />

          {
          user?.role === 'org' && 
          spaces.length > 1 && (
            <SpaceSelector
              value={selectedSpace}
              onChange={handleSpaceChange}
              onOpen={loadSpaces}
              displayEmpty
              renderValue={(selected) => selected ? selected.name : 'Select a Space'}
            >
              {spaces.map((space) => (
                <MenuItem key={space.id} value={space}>
                  {space.name}
                </MenuItem>
              ))}
            </SpaceSelector>
          )}
          <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1.5 }}>
            {/* <LanguagePopover /> */}
            {/* {user.role === 'university' && <HelpButtonPopover />} */}
            {/* <NotificationsPopover /> */}
            {/* <ContactsPopover /> */}
            {isDesktop && <MenuDesktop isOffset={isOffset} isHome={isHome} navConfig={finalConfig} />}


            {!isDesktop && <MenuMobile isOffset={isOffset} isHome={isHome} navConfig={finalConfig} drawerOpen={drawerOpen} handleDrawerClose={handleDrawerClose}/>}
            {/* <CreditsIndicator /> */}
            <AccountPopover />
          </Stack>
        </Container>
      </Toolbar>

      {isOffset && <ToolbarShadowStyle />}

      {user.isOTPVerified === false &&
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }} bgcolor={theme.palette.warning.main}>
          <Typography
            variant="contained"
            color='warning'
          >
            {'Your account has not been verified yet, '}
            <Link
              variant="subtitle2"
              color='white'
              sx={{ cursor: 'pointer', textDecoration: 'bold' }}
              onClick={() => dispatch(openModal('verifyCode'))}>
              Click here to verify your account
            </Link>
          </Typography>
        </Box>
      }
    </RootStyle>

  );
}